import QRCode from "qrcode";
import { useMemo } from "react";
import { Image, Text, View } from "react-native";
import Svg, { Path } from "react-native-svg";

import {
  backInMinutes,
  boardLayout,
  boardPrice,
  cardSubtitle,
  conditionTag,
  featuredAt,
  type TableDisplayBoard as Board,
  type TableDisplayItem,
  unitFor,
} from "@/lib/shows/tableDisplay";
import { FIXED_NAVY, palette } from "@/theme/palette";

/** The board is always dark: it sits on a monitor in a lit hall, whatever theme the device uses. */
const INK = palette.dark.foreground;
const MUTED = palette.dark.mutedForeground;
const GROUND = palette.dark.bg;
const SIGNAL = palette.dark.primary;
const BAR = FIXED_NAVY;
const PAPER = palette.light.bg;
const PAPER_INK = palette.light.foreground;

const BOLD = "BricolageGrotesque_700Bold";
const SEMI = "BricolageGrotesque_600SemiBold";
const REGULAR = "BricolageGrotesque_400Regular";
const TABULAR = ["tabular-nums" as const];

/**
 * A QR as one SVG path, built synchronously so the board never paints a blank code. Error
 * correction L keeps the modules as large as the code allows, which is what a phone needs at 1 m.
 */
function qrPath(url: string): { d: string; size: number; } | null {
  try {
    const { modules } = QRCode.create(url, { errorCorrectionLevel: "L" });
    let d = "";
    for (let y = 0; y < modules.size; y++) {
      for (let x = 0; x < modules.size; x++) {
        if (modules.get(y, x)) d += `M${x} ${y}h1v1h-1z`;
      }
    }
    return { d, size: modules.size };
  } catch {
    return null;
  }
}

function Qr(
  { url, size, label, testID }: { url: string; size: number; label: string; testID: string; },
) {
  const code = useMemo(() => qrPath(url), [url]);
  const pad = size * 0.08;
  // A whole number of pixels per module, so adjacent modules meet with no seam.
  const side = code ? Math.max(1, Math.floor(size / code.size)) * code.size : size;
  return (
    <View
      testID={testID}
      accessibilityRole="image"
      accessibilityLabel={label}
      style={{ backgroundColor: "#ffffff", padding: pad, borderRadius: size * 0.08 }}
    >
      {code
        ? (
          <Svg width={side} height={side} viewBox={`0 0 ${code.size} ${code.size}`}>
            <Path d={code.d} fill="#000000" />
          </Svg>
        )
        : <View style={{ width: side, height: side }} />}
    </View>
  );
}

/** A price that steps down one size per extra digit group instead of ever being cut off. */
function BoardPrice({ value, size, u }: { value: string; size: number; u: number; }) {
  const step = value.length <= 6 ? 1 : value.length <= 8 ? 0.8 : 0.62;
  return (
    <Text
      testID="table-display-featured-price"
      accessibilityLabel={value}
      style={{
        color: "#ffffff",
        fontFamily: BOLD,
        fontSize: size * step * u,
        lineHeight: size * step * u * 1.05,
        fontVariant: TABULAR,
      }}
    >
      {value}
    </Text>
  );
}

function Art(
  { item, width, u, radius }: { item: TableDisplayItem; width: number; u: number; radius: number; },
) {
  const height = width * 1.4;
  return (
    <View
      style={{
        width: width * u,
        height: height * u,
        borderRadius: radius * u,
        overflow: "hidden",
        backgroundColor: palette.dark.surface,
      }}
    >
      {item.image_url
        ? (
          <Image
            source={{ uri: item.image_url }}
            accessibilityLabel={item.product_name}
            resizeMode="contain"
            style={{ width: "100%", height: "100%" }}
          />
        )
        : null}
    </View>
  );
}

function FeaturedCard(
  { item, u, tall, hidePrices }: {
    item: TableDisplayItem;
    u: number;
    tall: boolean;
    hidePrices: boolean;
  },
) {
  const price = hidePrices ? null : boardPrice(item);
  const tag = conditionTag(item);
  const subtitle = cardSubtitle(item);
  const lastCopy = item.qty === 1;
  return (
    <View
      style={{
        flexDirection: "row",
        gap: 36 * u,
        marginTop: (tall ? 12 : 24) * u,
        alignItems: "flex-start",
      }}
    >
      <Art item={item} width={tall ? 140 : 190} u={u} radius={18} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          testID="table-display-featured-name"
          numberOfLines={2}
          style={{
            color: INK,
            fontFamily: BOLD,
            fontSize: (tall ? 40 : 50) * u,
            lineHeight: (tall ? 42 : 52) * u,
          }}
        >
          {item.product_name}
        </Text>
        {subtitle
          ? (
            <Text
              numberOfLines={1}
              style={{ color: MUTED, fontFamily: REGULAR, fontSize: 28 * u, marginTop: 6 * u }}
            >
              {subtitle}
            </Text>
          )
          : null}
        {price
          ? (
            <View style={{ marginTop: 14 * u }}>
              <BoardPrice value={price} size={tall ? 64 : 96} u={u} />
            </View>
          )
          : null}
        <View style={{ flexDirection: "row", gap: 12 * u, marginTop: 14 * u, flexWrap: "wrap" }}>
          {tag ? <Pill label={tag} u={u} /> : null}
          {lastCopy
            ? <Pill label="One copy" u={u} />
            : <Pill label={`${item.qty} available`} u={u} />}
        </View>
      </View>
    </View>
  );
}

function Pill({ label, u }: { label: string; u: number; }) {
  return (
    <View
      style={{
        backgroundColor: SIGNAL,
        borderRadius: 999,
        paddingHorizontal: 20 * u,
        paddingVertical: 6 * u,
      }}
    >
      <Text style={{ color: PAPER_INK, fontFamily: SEMI, fontSize: 26 * u }}>{label}</Text>
    </View>
  );
}

function JustInRow(
  { item, u, hidePrices }: { item: TableDisplayItem; u: number; hidePrices: boolean; },
) {
  const price = hidePrices ? null : boardPrice(item);
  const tag = conditionTag(item);
  const detail = [item.set_name, tag].filter(Boolean).join(" · ");
  return (
    <View
      testID="table-display-just-in-row"
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 16 * u,
        paddingVertical: 5 * u,
        borderBottomWidth: 1,
        borderBottomColor: palette.dark.border,
      }}
    >
      <Art item={item} width={50} u={u} radius={8} />
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text numberOfLines={1} style={{ color: INK, fontFamily: BOLD, fontSize: 26 * u }}>
          {item.product_name}
        </Text>
        {detail
          ? (
            <Text numberOfLines={1} style={{ color: MUTED, fontFamily: REGULAR, fontSize: 20 * u }}>
              {detail}
            </Text>
          )
          : null}
      </View>
      {price
        ? (
          <Text style={{ color: INK, fontFamily: BOLD, fontSize: 34 * u, fontVariant: TABULAR }}>
            {price}
          </Text>
        )
        : null}
    </View>
  );
}

/** The Hot entries a strip shows at this tick: all of them when they fit, else a window that rotates through them. */
export function buyingAt<T>(entries: readonly T[], tick: number, room: number): T[] {
  if (entries.length <= room) return [...entries];
  return Array.from({ length: room }, (_, i) => entries[(tick + i) % entries.length]);
}

function BuyingStrip(
  { board, u, tall, tick }: { board: Board; u: number; tall: boolean; tick: number; },
) {
  if (board.buying.length === 0) return null;
  return (
    <View
      testID="table-display-buying"
      style={{
        backgroundColor: SIGNAL,
        borderRadius: 24 * u,
        paddingVertical: (tall ? 8 : 16) * u,
        paddingHorizontal: 22 * u,
      }}
    >
      <Text
        style={{ color: PAPER_INK, fontFamily: BOLD, fontSize: 22 * u, letterSpacing: 0.4 * u }}
      >
        WE'RE BUYING
      </Text>
      {buyingAt(board.buying, tick, tall ? 1 : 2).map((entry) => (
        <View
          key={`${entry.name}-${entry.set_name ?? ""}`}
          testID="table-display-buying-row"
          style={{ flexDirection: "row", alignItems: "baseline", gap: 12 * u }}
        >
          <Text
            numberOfLines={1}
            style={{ flex: 1, minWidth: 0, color: PAPER_INK, fontFamily: SEMI, fontSize: 26 * u }}
          >
            {entry.name}
          </Text>
          {
            /* The amount has its own text, so a long name is cut and the cash never is. */
          }
          {entry.cash
            ? (
              <Text
                testID="table-display-buying-cash"
                style={{
                  flexShrink: 0,
                  color: PAPER_INK,
                  fontFamily: BOLD,
                  fontSize: 26 * u,
                  fontVariant: TABULAR,
                }}
              >
                {`$${entry.cash} cash`}
              </Text>
            )
            : null}
        </View>
      ))}
    </View>
  );
}

function Message(
  { testID, title, body, tone, u, children }: {
    testID: string;
    title: string;
    body?: string;
    tone: "dark" | "paper";
    u: number;
    children?: React.ReactNode;
  },
) {
  return (
    <View
      testID={testID}
      style={{
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: tone === "dark" ? GROUND : PAPER,
        padding: 40 * u,
        gap: 18 * u,
      }}
    >
      <Text
        accessibilityRole="header"
        style={{
          color: tone === "dark" ? INK : PAPER_INK,
          fontFamily: BOLD,
          fontSize: 64 * u,
          lineHeight: 68 * u,
          textAlign: "center",
        }}
      >
        {title}
      </Text>
      {body
        ? (
          <Text
            style={{
              color: tone === "dark" ? MUTED : PAPER_INK,
              fontFamily: REGULAR,
              fontSize: 30 * u,
              textAlign: "center",
              maxWidth: 900 * u,
            }}
          >
            {body}
          </Text>
        )
        : null}
      {children}
    </View>
  );
}

/**
 * The Table display board: what a Vendor's monitor shows to everyone walking by. It is drawn on a
 * 720-tall canvas and scaled by the viewport height (`unitFor`), so the same layout reads from 1 m
 * on a 15.6-inch panel and from across the aisle on a larger screen; the smallest text is 20 of 720
 * (2.8%). A wide viewport puts the featured card beside the just-in list; a 4:3 one (an iPad
 * mirrored) stacks them. It takes no input: every control is absent.
 *
 * `reconnecting` replaces the whole board, so a price drawn before the connection dropped is never
 * shown as live. `tick` picks the featured card and wraps around the list.
 */
export function TableDisplayBoard(
  { board, width, height, tick, reconnecting }: {
    board: Board | null;
    width: number;
    height: number;
    tick: number;
    reconnecting: boolean;
  },
) {
  const u = unitFor(height);
  const tall = boardLayout(width, height) === "tall";
  const qrSize = (tall ? 84 : 132) * u;
  if (reconnecting || !board) {
    return (
      <Message
        testID="table-display-reconnecting"
        tone="dark"
        u={u}
        title="Reconnecting…"
        body="Prices are hidden until we're live again, so you never see an old one."
      />
    );
  }
  if (board.state === "paused") {
    const minutes = backInMinutes(board.resume_in_seconds ?? 60);
    return (
      <Message
        testID="table-display-paused"
        tone="dark"
        u={u}
        title={`Back in ${minutes} min`}
        body="We stepped away. Cards are still here when we're back."
      />
    );
  }
  if (board.state === "closed") {
    return (
      <Message
        testID="table-display-closed"
        tone="paper"
        u={u}
        title="Thanks for stopping by"
        body={board.shop_url ? `${board.shop_name}. Scan to follow the shop.` : board.shop_name}
      >
        {board.shop_url
          ? (
            <Qr
              url={board.shop_url}
              size={qrSize * 1.4}
              label="QR code for the shop"
              testID="table-display-shop-qr"
            />
          )
          : null}
      </Message>
    );
  }

  const hidePrices = board.price_visibility === "none";
  const featured = featuredAt(board.featured, tick);
  const activeIndex = board.featured.length === 0 ? 0 : tick % board.featured.length;
  return (
    <View testID="table-display-live" style={{ flex: 1, backgroundColor: GROUND }}>
      <View style={{ flex: 1, flexDirection: tall ? "column" : "row", minHeight: 0 }}>
        <View
          style={{
            flex: tall ? undefined : 1.45,
            paddingTop: (tall ? 16 : 40) * u,
            paddingHorizontal: (tall ? 40 : 56) * u,
            paddingBottom: 12 * u,
            minHeight: 0,
            minWidth: 0,
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "center", gap: 12 * u }}>
            <View
              style={{
                width: 14 * u,
                height: 14 * u,
                borderRadius: 7 * u,
                backgroundColor: SIGNAL,
              }}
            />
            <Text
              numberOfLines={1}
              style={{ color: SIGNAL, fontFamily: SEMI, fontSize: 28 * u, flexShrink: 1 }}
            >
              {board.shop_name}
            </Text>
          </View>
          {featured
            ? <FeaturedCard item={featured} u={u} tall={tall} hidePrices={hidePrices} />
            : (
              <Text style={{ color: INK, fontFamily: BOLD, fontSize: 56 * u, marginTop: 40 * u }}>
                Stock is being set up
              </Text>
            )}
          {board.featured.length > 1
            ? (
              <View style={{ flexDirection: "row", gap: 12 * u, marginTop: (tall ? 10 : 22) * u }}>
                {board.featured.map((item, index) => (
                  <View
                    key={item.public_id}
                    style={{
                      width: (index === activeIndex ? 40 : 14) * u,
                      height: 14 * u,
                      borderRadius: 8 * u,
                      backgroundColor: index === activeIndex ? SIGNAL : "rgba(238,241,251,0.3)",
                    }}
                  />
                ))}
              </View>
            )
            : null}
        </View>
        <View
          style={{
            flex: tall ? undefined : 1,
            paddingTop: (tall ? 0 : 40) * u,
            paddingHorizontal: (tall ? 40 : 40) * u,
            paddingBottom: 12 * u,
            gap: (tall ? 4 : 10) * u,
            minHeight: 0,
            borderLeftWidth: tall ? 0 : 1,
            borderLeftColor: palette.dark.border,
          }}
        >
          <Text style={{ color: MUTED, fontFamily: SEMI, fontSize: 28 * u }}>Just in</Text>
          <View
            style={tall ? { flexDirection: "row", flexWrap: "wrap", columnGap: 32 * u } : undefined}
          >
            {board.just_in.map((item) => (
              <View key={item.public_id} style={tall ? { width: "48%" } : undefined}>
                <JustInRow item={item} u={u} hidePrices={hidePrices} />
              </View>
            ))}
          </View>
          <View style={{ marginTop: "auto" }}>
            <BuyingStrip board={board} u={u} tall={tall} tick={tick} />
          </View>
        </View>
      </View>
      <View
        testID="table-display-bar"
        style={{
          backgroundColor: BAR,
          flexDirection: "row",
          alignItems: "center",
          gap: 24 * u,
          paddingHorizontal: (tall ? 40 : 56) * u,
          paddingVertical: (tall ? 8 : 14) * u,
        }}
      >
        <View
          style={{
            width: 46 * u,
            height: 46 * u,
            borderRadius: 23 * u,
            borderWidth: 4 * u,
            borderColor: SIGNAL,
          }}
        />
        <Text style={{ color: INK, fontFamily: SEMI, fontSize: 32 * u, flex: 1 }}>
          Scan to search every card on this table
        </Text>
        {board.browse_url
          ? (
            <Qr
              url={board.browse_url}
              size={qrSize}
              label="QR code to search this table on your phone"
              testID="table-display-qr"
            />
          )
          : null}
      </View>
    </View>
  );
}
